import type { ItineraryItem } from '@travel-planner/shared/schemas';
import { resolveThumbnailUrl } from '@travel-planner/shared/utils';

// Resolves a renderable image URL for an itinerary item, matching the
// customer Preview's resolution order (apps/preview/.../preview-item-card.tsx)
// so the planner and the preview always show the same main photo:
//   1. The IMAGE attachment flagged `isThumbnail` in `item.attachments` jsonb.
//   2. The first IMAGE attachment by `order` (then array order).
//   3. The denormalised `item.thumbnail` text column — a point-in-time
//      snapshot taken when the product was added, so it goes LAST: when a
//      product's main photo is later changed, this column is stale while the
//      snapshotted `attachments` still describe the current image set. (This
//      is why the old photo used to linger here but not in Preview.)
// Returns `null` when nothing usable is present, so the row can fall back to
// a category icon glyph.

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const isImageAttachment = (entry: Record<string, unknown>): boolean =>
  typeof entry.type !== 'string' || entry.type.toUpperCase() === 'IMAGE';

const attachmentUrl = (entry: Record<string, unknown>): string | null => {
  if (typeof entry.fileKey === 'string' && entry.fileKey.length > 0) {
    const url = resolveThumbnailUrl(entry.fileKey);
    if (url) return url;
  }
  if (typeof entry.url === 'string' && entry.url.length > 0) {
    return entry.url;
  }
  return null;
};

export const resolveItemImageUrl = (
  item: Pick<ItineraryItem, 'attachments' | 'thumbnail'>
): string | null => {
  if (Array.isArray(item.attachments)) {
    const images = item.attachments
      .filter(
        (entry): entry is Record<string, unknown> =>
          isRecord(entry) && isImageAttachment(entry)
      )
      // Mirror Preview's `narrowAttachments`: render in `order` (ascending),
      // falling back to original array position when `order` is absent.
      .map((entry, idx) => ({ entry, idx }))
      .sort((a, b) => {
        const orderA =
          typeof a.entry.order === 'number' ? a.entry.order : a.idx;
        const orderB =
          typeof b.entry.order === 'number' ? b.entry.order : b.idx;
        return orderA - orderB;
      })
      .map(({ entry }) => entry);

    const thumbnail = images.find((entry) => entry.isThumbnail === true);
    if (thumbnail) {
      const url = attachmentUrl(thumbnail);
      if (url) return url;
    }

    for (const entry of images) {
      const url = attachmentUrl(entry);
      if (url) return url;
    }
  }

  // Stale-by-design snapshot column — only used when no image attachment is
  // present (e.g. short-form rentals/transfers carrying just a thumbnail).
  return resolveThumbnailUrl(item.thumbnail);
};
